<template>
  <div class="modal" :class="{ 'show d-block': show }">
    <div class="modal-dialog modal-lg">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">
            <i class="fas fa-envelope me-2"></i>
            Send Email
          </h5>
          <button type="button" class="btn-close" @click="closeModal"></button>
        </div>
        <form @submit.prevent="sendEmail">
          <div class="modal-body">
            <div class="mb-3">
              <label class="form-label">To <span class="text-danger">*</span></label>
              <input
                type="email"
                class="form-control"
                v-model="email.to"
                placeholder="recipient@example.com"
                required
              />
              <small class="text-muted">Recipient email address</small>
            </div>

            <div class="mb-3">
              <label class="form-label">Subject <span class="text-danger">*</span></label>
              <input
                type="text"
                class="form-control"
                v-model="email.subject"
                placeholder="Email subject"
                required
              />
            </div>

            <div class="mb-3">
              <label class="form-label">Message <span class="text-danger">*</span></label>
              <textarea
                class="form-control"
                v-model="email.body"
                rows="10"
                placeholder="Email message..."
                required
              ></textarea>
            </div>

            <!-- Attachments section hidden for now
            <div class="mb-3">
              <label class="form-label">Attachments</label>
              <div v-if="email.attachments.length > 0" class="mb-2">
                <div v-for="(attachment, index) in email.attachments" :key="index" 
                     class="d-flex align-items-center justify-content-between border p-2 mb-2">
                  <span>
                    <i class="fas fa-file-pdf text-danger me-2"></i>
                    {{ attachment.name }}
                  </span>
                  <button
                    type="button"
                    class="btn btn-sm btn-outline-danger"
                    @click="removeAttachment(index)"
                  >
                    <i class="fas fa-times"></i>
                  </button>
                </div>
              </div>
              <div v-else class="text-muted">
                <small>No attachments</small>
              </div>
            </div>
            -->
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeModal">Cancel</button>
            <button type="submit" class="btn btn-primary" :disabled="sending">
              <i class="fas fa-paper-plane me-2"></i>
              {{ sending ? 'Sending...' : 'Send Email' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
  <div class="modal-backdrop fade show" v-if="show"></div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue';
import Api from '@/services/ApiService';
import Alert from '@/services/Alert';

interface Attachment {
  name: string;
  url?: string;
  fileId?: string;
}

interface EmailData {
  to: string;
  subject: string;
  body: string;
  attachments: Attachment[];
  pdfBlob?: Blob;
  pdfFileName?: string;
}

const props = defineProps<{
  show: boolean;
  initialData?: Partial<EmailData>;
}>();

const emit = defineEmits<{
  close: [];
  sent: [];
}>();

const email = ref<EmailData>({
  to: '',
  subject: '',
  body: '',
  attachments: []
});

const sending = ref(false);

// Watch for initial data changes
watch(() => props.initialData, (newData) => {
  if (newData) {
    email.value = {
      to: newData.to || '',
      subject: newData.subject || '',
      body: newData.body || '',
      attachments: newData.attachments || []
    };
  }
}, { immediate: true, deep: true });

const closeModal = () => {
  emit('close');
  // Reset form after a short delay
  setTimeout(() => {
    email.value = {
      to: '',
      subject: '',
      body: '',
      attachments: []
    };
  }, 300);
};

const removeAttachment = (index: number) => {
  email.value.attachments.splice(index, 1);
};

const sendEmail = async () => {
  sending.value = true;
  try {
    // If we have a PDF blob, send it with the email
    if (props.initialData?.pdfBlob) {
      const formData = new FormData();
      formData.append('to', email.value.to);
      formData.append('subject', email.value.subject);
      formData.append('body', email.value.body);
      formData.append('pdfFile', props.initialData.pdfBlob, props.initialData.pdfFileName || 'invoice.pdf');
      
      await Api.sendEmailWithAttachment(formData);
    } else {
      // Send without attachment
      await Api.sendEmail({
        to: email.value.to,
        subject: email.value.subject,
        body: email.value.body
      });
    }
    
    Alert.notify({ text: 'Email queued successfully!', type: 'success' });
    emit('sent');
    closeModal();
  } catch (error: any) {
    console.error('Error sending email:', error);
    Alert.notify({ text: 'Failed to send email: ' + (error.message || 'Unknown error'), type: 'error' });
  } finally {
    sending.value = false;
  }
};
</script>

<style scoped>
.modal {
  background-color: rgba(0, 0, 0, 0.5);
}
</style>
